import type { TenantKey } from '@/services/auth-api';

/**
 * Factory coordinates for geofence warnings on clock-in.
 *
 * **Not authoritative** — backend does not validate GPS. We only use this
 * to warn a worker when their device GPS is far from the factory (typo'd
 * tenant, clocking from home, etc). Never blocks — worker can always confirm.
 *
 * Real-world coords should come from a per-tenant settings endpoint. For
 * now these are approximate industrial-park centers per tenant.
 */
interface FactoryCoord {
  lat: number;
  lng: number;
  /** Radius in meters — outside this prompts a confirm. */
  radiusM: number;
}

const FACTORY: Record<TenantKey, FactoryCoord> = {
  WITTY: { lat: 22.3193, lng: 114.1694, radiusM: 300 },
  PSP: { lat: 10.9454, lng: 106.8244, radiusM: 500 },
  YPC: { lat: 11.5564, lng: 104.9282, radiusM: 500 },
};

function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export type GeofenceCheck =
  | { ok: true; distanceM: number; lat: number; lng: number }
  | { ok: false; reason: 'denied' | 'unavailable' | 'timeout' | 'too-far'; distanceM?: number };

/**
 * Check if the device is within the factory fence for `tenant`. Resolves
 * quickly (6s timeout) — worker shouldn't wait long to clock in.
 */
export async function checkGeofence(tenant: TenantKey): Promise<GeofenceCheck> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { ok: false, reason: 'unavailable' };
  }
  const fence = FACTORY[tenant];
  return new Promise<GeofenceCheck>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const d = haversineMeters(pos.coords.latitude, pos.coords.longitude, fence.lat, fence.lng);
        if (d <= fence.radiusM) {
          resolve({
            ok: true,
            distanceM: Math.round(d),
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        } else resolve({ ok: false, reason: 'too-far', distanceM: Math.round(d) });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) resolve({ ok: false, reason: 'denied' });
        else if (err.code === err.TIMEOUT) resolve({ ok: false, reason: 'timeout' });
        else resolve({ ok: false, reason: 'unavailable' });
      },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 60_000 },
    );
  });
}
